/**
 * FoulingDriftPage.tsx — 10-year membrane fouling & SOTE drift simulator
 *
 * Models the trajectory of:
 *   - Bubble Ø (creep due to membrane stretching)
 *   - SOTE per metre (drift from fine-bubble band)
 *   - Diffuser back-pressure rise
 *   - Recovery-cleaning recovery efficiency
 *
 * References:
 *   Rosso & Stenstrom (2006). Surfactant effects on alpha-factors in aeration.
 *   Wagner & Stenstrom (2014). Aeration and mixing in wastewater treatment.
 *   EPA (1989). Design Manual: Fine pore aeration systems. EPA/625/1-89/023.
 *   Sanitaire / Aerzen / Diffuser membrane creep curves (typical envelopes).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { TrendingUp, ArrowLeft, RefreshCcw, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';

const KEY = 'rb_fouling_drift_v1';

interface FoulingState {
  diffuserType: 'epdm' | 'ptfe' | 'ceramic' | 'pu';
  ironMgL: number;
  calciumMgL: number;
  nomMgL: number;
  cleaningIntervalMonths: number;
  cleaningRecoveryPct: number;
  horizonYears: number;
  initialBubbleMm: number;
  initialSOTEperM: number;
  initialPressureKPa: number;
}

const DEFAULT_STATE: FoulingState = {
  diffuserType: 'epdm',
  ironMgL: 0.6,
  calciumMgL: 90,
  nomMgL: 5.0,
  cleaningIntervalMonths: 18,
  cleaningRecoveryPct: 85,
  horizonYears: 10,
  initialBubbleMm: 2.5,
  initialSOTEperM: 5.0,
  initialPressureKPa: 1.5,
};

// Type-specific creep characteristics (monthly drift rates)
const TYPE_RATES: Record<FoulingState['diffuserType'], { creep: number; pressureRise: number; foulingAccel: number }> = {
  epdm:    { creep: 0.020, pressureRise: 0.030, foulingAccel: 1.0 },
  ptfe:    { creep: 0.008, pressureRise: 0.012, foulingAccel: 0.4 },
  ceramic: { creep: 0.004, pressureRise: 0.025, foulingAccel: 0.7 },
  pu:      { creep: 0.014, pressureRise: 0.020, foulingAccel: 0.8 },
};

interface YearPoint {
  month: number;
  year: number;
  bubbleMm: number;
  sotePerM: number;
  pressureKPa: number;
  cleaned: boolean;
}

function fmt(n: number, d = 2) { if (!isFinite(n)) return '—'; return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }); }

function simulate(s: FoulingState): YearPoint[] {
  const rates = TYPE_RATES[s.diffuserType];
  // Water-chemistry penalty: iron and calcium accelerate fouling
  const chemFactor = 1 + 0.4 * (s.ironMgL / 0.5) + 0.2 * (s.calciumMgL / 100) + 0.3 * (s.nomMgL / 5);
  const totalMonths = s.horizonYears * 12;
  const pts: YearPoint[] = [];
  let bubble = s.initialBubbleMm;
  let sote = s.initialSOTEperM;
  let pressure = s.initialPressureKPa;
  for (let m = 0; m <= totalMonths; m++) {
    let cleaned = false;
    if (m > 0 && m % s.cleaningIntervalMonths === 0) {
      // Recovery on cleaning
      const recovery = s.cleaningRecoveryPct / 100;
      bubble = s.initialBubbleMm + (bubble - s.initialBubbleMm) * (1 - recovery);
      sote = s.initialSOTEperM + (sote - s.initialSOTEperM) * (1 - recovery);
      pressure = s.initialPressureKPa + (pressure - s.initialPressureKPa) * (1 - recovery);
      cleaned = true;
    } else {
      // Monthly drift
      bubble += s.initialBubbleMm * rates.creep * rates.foulingAccel * chemFactor / 12;
      sote -= s.initialSOTEperM * rates.creep * rates.foulingAccel * chemFactor / 12;
      pressure += s.initialPressureKPa * rates.pressureRise * rates.foulingAccel * chemFactor / 12;
    }
    pts.push({ month: m, year: m / 12, bubbleMm: bubble, sotePerM: Math.max(0.5, sote), pressureKPa: pressure, cleaned });
  }
  return pts;
}

export default function FoulingDriftPage() {
  const navigate = useNavigate();
  const [s, setS] = useState<FoulingState>(() => loadPersisted<FoulingState>(KEY, DEFAULT_STATE));
  const [, setNonce] = useState(0);

  useEffect(() => { savePersisted(KEY, s); }, [s]);

  useEffect(() => {
    const unsub = onPersistChange(k => { if (k === KEYS.DIFFUSER_MODULE) setNonce(n => n + 1); });
    return () => unsub();
  }, []);

  const sim = useMemo(() => simulate(s), [s]);
  const final = sim[sim.length - 1];
  const meanSOTE = sim.reduce((a, p) => a + p.sotePerM, 0) / sim.length;
  const replacementYear = useMemo(() => {
    // Replace when SOTE drops below 60% of initial — typical industry trigger
    const trigger = s.initialSOTEperM * 0.6;
    const pt = sim.find(p => p.sotePerM <= trigger);
    return pt ? pt.year : null;
  }, [sim, s.initialSOTEperM]);

  // SVG chart of SOTE & pressure over time
  const svgW = 720, svgH = 280;
  const padL = 45, padR = 50, padT = 20, padB = 30;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const xMax = s.horizonYears;
  const x = (yr: number) => padL + (yr / xMax) * plotW;
  const yL = (v: number, maxV: number) => padT + plotH - (v / maxV) * plotH;
  const maxSOTE = s.initialSOTEperM * 1.2;
  const maxP = Math.max(...sim.map(p => p.pressureKPa)) * 1.1;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={TrendingUp}
        title="Membrane Fouling & SOTE Drift (10-year)"
        subtitle="Type-dependent drift trajectory with recovery cleanings and replacement trigger"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Year-{s.horizonYears} bubble Ø</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(final.bubbleMm, 2)} mm</div>
          <div className="text-xs text-slate-500 mt-1">from {s.initialBubbleMm} mm</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Year-{s.horizonYears} SOTE/m</div>
          <div className="text-2xl font-bold text-emerald-300 mt-1">{fmt(final.sotePerM, 2)} %</div>
          <div className="text-xs text-slate-500 mt-1">mean over horizon {fmt(meanSOTE, 2)} %</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Year-{s.horizonYears} back-pressure</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{fmt(final.pressureKPa, 2)} kPa</div>
        </div>
        <div className={`rounded-xl border p-4 ${
          replacementYear === null ? 'border-emerald-700 bg-emerald-900/20' : 'border-red-700 bg-red-900/20'
        }`}>
          <div className="text-xs uppercase tracking-wider text-slate-400">Replacement trigger</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">
            {replacementYear === null ? 'Beyond horizon' : `Year ${fmt(replacementYear, 1)}`}
          </div>
          <div className="text-xs text-slate-500 mt-1">SOTE &lt; 60 % of initial</div>
        </div>
      </div>

      {/* Inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Inputs</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Diffuser membrane type</label>
            <select value={s.diffuserType}
              onChange={e => setS({ ...s, diffuserType: e.target.value as FoulingState['diffuserType'] })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100">
              <option value="epdm">EPDM (industry standard)</option>
              <option value="ptfe">PTFE laminate</option>
              <option value="ceramic">Ceramic / sintered</option>
              <option value="pu">Polyurethane</option>
            </select>
          </div>
          {[
            ['ironMgL', 'Iron (mg/L)', 0.05],
            ['calciumMgL', 'Calcium (mg/L)', 5],
            ['nomMgL', 'NOM/TOC (mg/L)', 0.5],
            ['cleaningIntervalMonths', 'Cleaning interval (months)', 1],
            ['cleaningRecoveryPct', 'Recovery per cleaning (%)', 1],
            ['horizonYears', 'Horizon (years)', 1],
            ['initialBubbleMm', 'Initial bubble Ø (mm)', 0.1],
            ['initialSOTEperM', 'Initial SOTE/m (%)', 0.1],
            ['initialPressureKPa', 'Initial back-pressure (kPa)', 0.1],
          ].map(([k, label, step]) => (
            <div key={k as string}>
              <label className="text-xs text-slate-400 block mb-1">{label as string}</label>
              <input type="number" step={step as number}
                value={s[k as keyof FoulingState] as number}
                onChange={e => setS({ ...s, [k as string]: Number(e.target.value) } as FoulingState)}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Drift trajectory</div>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          {/* SOTE replacement threshold */}
          <line x1={padL} y1={yL(s.initialSOTEperM * 0.6, maxSOTE)} x2={padL + plotW} y2={yL(s.initialSOTEperM * 0.6, maxSOTE)}
            stroke="#ef4444" strokeWidth={0.7} strokeDasharray="3 3" />
          <text x={padL + plotW + 3} y={yL(s.initialSOTEperM * 0.6, maxSOTE) + 3} fontSize={8} fill="#ef4444">replace</text>
          {/* SOTE line (left axis) */}
          <polyline fill="none" stroke="#22c55e" strokeWidth={2}
            points={sim.map(p => `${x(p.year)},${yL(p.sotePerM, maxSOTE)}`).join(' ')} />
          {/* Pressure line (right axis scaled to same plot height) */}
          <polyline fill="none" stroke="#f59e0b" strokeWidth={1.5}
            points={sim.map(p => `${x(p.year)},${yL(p.pressureKPa, maxP)}`).join(' ')} />
          {/* Cleaning markers */}
          {sim.filter(p => p.cleaned).map((p, i) => (
            <line key={i} x1={x(p.year)} y1={padT} x2={x(p.year)} y2={padT + plotH}
              stroke="#38bdf8" strokeWidth={0.5} strokeDasharray="2 2" />
          ))}
          {/* X labels */}
          {Array.from({ length: s.horizonYears + 1 }, (_, i) => i).map(yr => (
            <g key={yr}>
              <text x={x(yr)} y={padT + plotH + 15} textAnchor="middle" fontSize={9} fill="#94a3b8">y{yr}</text>
            </g>
          ))}
          {/* Y axis labels */}
          {[0, 25, 50, 75, 100].map(p => {
            const v = (p / 100) * maxSOTE;
            return (
              <text key={p} x={padL - 4} y={yL(v, maxSOTE) + 3} textAnchor="end" fontSize={8} fill="#22c55e">
                {v.toFixed(1)}%
              </text>
            );
          })}
          {[0, 25, 50, 75, 100].map(p => {
            const v = (p / 100) * maxP;
            return (
              <text key={p} x={padL + plotW + 4} y={yL(v, maxP) + 3} fontSize={8} fill="#f59e0b">
                {v.toFixed(1)}kPa
              </text>
            );
          })}
          {/* Legend */}
          <g transform={`translate(${padL + 8}, 5)`}>
            <line x1={0} y1={5} x2={18} y2={5} stroke="#22c55e" strokeWidth={2} />
            <text x={22} y={8} fontSize={9} fill="#22c55e">SOTE/m</text>
            <line x1={70} y1={5} x2={88} y2={5} stroke="#f59e0b" strokeWidth={1.5} />
            <text x={92} y={8} fontSize={9} fill="#f59e0b">Pressure</text>
            <line x1={150} y1={5} x2={168} y2={5} stroke="#38bdf8" strokeWidth={0.5} strokeDasharray="2 2" />
            <text x={172} y={8} fontSize={9} fill="#38bdf8">Cleaning</text>
          </g>
        </svg>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Interpretation
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">
          The replacement trigger (SOTE &lt; 60 % of initial) is a typical industry threshold. Below
          this, the OPEX of running the diffusers outweighs the CAPEX of replacement. Cleaning interval
          and recovery efficiency are calibratable: post-award pilot data updates both, and the curves
          recompute. Type-dependent creep rates are typical envelopes from EPDM/PTFE/ceramic/PU vendor
          literature — final figures use the supplier's quoted 10-year drift curve.
        </div>
      </div>
    </div>
  );
}
